import { LoadingState } from '../../domain/schemas';
import { SettingsPlansDetails } from '../../domain/settingsPlansDetails';

// Action Types

const FETCH_SETTINGS_PLANS_DETAILS = 'FETCH_SETTINGS_PLANS_DETAILS' as const;
const FETCH_SETTINGS_PLANS_DETAILS_REQUEST =
  'FETCH_SETTINGS_PLANS_DETAILS_REQUEST' as const;
const FETCH_SETTINGS_PLANS_DETAILS_SUCCESS =
  'FETCH_SETTINGS_PLANS_DETAILS_SUCCESS' as const;
const RENEW_SETTINGS_PLANS_DETAILS = 'RENEW_SETTINGS_PLANS_DETAILS' as const;
const PATCH_SETTINGS_PLANS_PREDICT_REQUEST =
  'PATCH_SETTINGS_PLANS_PREDICT_REQUEST' as const;
const PATCH_SETTINGS_PLANS_PREDICT_SUCCESS =
  'PATCH_SETTINGS_PLANS_PREDICT_SUCCESS' as const;
const PATCH_SETTINGS_PLANS_PREDICT_FAILURE =
  'PATCH_SETTINGS_PLANS_PREDICT_FAILURE' as const;
const FETCH_SETTINGS_PLANS_DETAILS_ON_YEAR_MONTH_CHANGE =
  'FETCH_SETTINGS_PLANS_DETAILS_ON_YEAR_MONTH_CHANGE' as const;
const FETCH_LATEST_SETTINGS_PLANS_DETAILS =
  'FETCH_LATEST_SETTINGS_PLANS_DETAILS' as const;
const FETCH_LATEST_SETTINGS_PLANS_DETAILS_REQUEST =
  'FETCH_LATEST_SETTINGS_PLANS_DETAILS_REQUEST' as const;
const FETCH_LATEST_SETTINGS_PLANS_DETAILS_SUCCESS =
  'FETCH_LATEST_SETTINGS_PLANS_DETAILS_SUCCESS' as const;
const FETCH_LATEST_SETTINGS_PLANS_DETAILS_FAILURE =
  'FETCH_LATEST_SETTINGS_PLANS_DETAILS_FAILURE' as const;
const CLEAR_LATEST_SETTINGS_PLANS_DETAILS =
  'CLEAR_LATEST_SETTINGS_PLANS_DETAILS' as const;

export const SettingsPlansDetailsActionTypes = {
  FETCH_SETTINGS_PLANS_DETAILS,
  FETCH_SETTINGS_PLANS_DETAILS_REQUEST,
  FETCH_SETTINGS_PLANS_DETAILS_SUCCESS,
  RENEW_SETTINGS_PLANS_DETAILS,
  PATCH_SETTINGS_PLANS_PREDICT_REQUEST,
  PATCH_SETTINGS_PLANS_PREDICT_SUCCESS,
  PATCH_SETTINGS_PLANS_PREDICT_FAILURE,
  FETCH_SETTINGS_PLANS_DETAILS_ON_YEAR_MONTH_CHANGE,
  FETCH_LATEST_SETTINGS_PLANS_DETAILS,
  FETCH_LATEST_SETTINGS_PLANS_DETAILS_REQUEST,
  FETCH_LATEST_SETTINGS_PLANS_DETAILS_SUCCESS,
  FETCH_LATEST_SETTINGS_PLANS_DETAILS_FAILURE,
  CLEAR_LATEST_SETTINGS_PLANS_DETAILS,
};

// Action Creators

type FetchSettingsPlansDetailsParams = SettingsPlansDetails['setting'] & {
  /**
   * 最新の予測値を取得するかどうか
   * trueの場合、GET /settings/plans/{year}/{month}/{hallCode}にisLatest=trueをクエリパラメータとして送信
   * 予測更新ボタンクリック時に使用
   */
  isLatest?: boolean;
};

/**
 * 予定詳細データを取得するアクション
 * @param params 店舗コード、年、月、およびisLatestフラグ
 * @returns Actionオブジェクト（paramsとqueryParamsを分離して保持）
 */
function fetchSettingsPlansDetailsAction(
  params: FetchSettingsPlansDetailsParams
) {
  const { isLatest, ...settingParams } = params;
  const queryParams: { isLatest?: boolean } =
    isLatest !== undefined ? { isLatest } : {};
  return {
    type: FETCH_SETTINGS_PLANS_DETAILS,
    payload: {
      params: settingParams,
      queryParams,
    },
  };
}

function fetchSettingsPlansDetailsRequestAction() {
  return {
    type: FETCH_SETTINGS_PLANS_DETAILS_REQUEST,
  };
}

function fetchSettingsPlansDetailsSuccessAction(
  settingsPlansDetails: SettingsPlansDetails
) {
  return {
    type: FETCH_SETTINGS_PLANS_DETAILS_SUCCESS,
    payload: { settingsPlansDetails },
  };
}

function renewSettingsPlansDetailsACtion() {
  return {
    type: RENEW_SETTINGS_PLANS_DETAILS,
  };
}

/**
 * 予測値を確定するアクション
 * PATCH /settings/plans/{hallCode}/predict を呼び出す
 * @param hallCode 店舗コード
 */
function patchSettingsPlansPredictRequestAction(hallCode: string) {
  return {
    type: PATCH_SETTINGS_PLANS_PREDICT_REQUEST,
    payload: { hallCode },
  };
}

function patchSettingsPlansPredictSuccessAction() {
  return {
    type: PATCH_SETTINGS_PLANS_PREDICT_SUCCESS,
  };
}

function patchSettingsPlansPredictFailureAction() {
  return {
    type: PATCH_SETTINGS_PLANS_PREDICT_FAILURE,
  };
}

/**
 * 年月変更時に予定詳細データを再取得するアクション
 * 店舗一覧の取得完了を待ってから、新しい年月で予定詳細データを取得する
 * @param year 年
 * @param month 月
 * @param hallCode 店舗コード
 */
function fetchSettingsPlansDetailsOnYearMonthChangeAction(
  year: number,
  month: number,
  hallCode: string
) {
  return {
    type: FETCH_SETTINGS_PLANS_DETAILS_ON_YEAR_MONTH_CHANGE,
    payload: { year, month, hallCode },
  };
}

/**
 * 最新の予測データを取得するアクション（isLatest=true）
 * 既存のsettingsPlansDetailsを上書きせず、latestSettingsPlansDetailsに保存する
 */
function fetchLatestSettingsPlansDetailsAction(
  params: SettingsPlansDetails['setting']
) {
  return {
    type: FETCH_LATEST_SETTINGS_PLANS_DETAILS,
    payload: { params },
  };
}

function fetchLatestSettingsPlansDetailsRequestAction() {
  return {
    type: FETCH_LATEST_SETTINGS_PLANS_DETAILS_REQUEST,
  };
}

function fetchLatestSettingsPlansDetailsSuccessAction(
  settingsPlansDetails: SettingsPlansDetails
) {
  return {
    type: FETCH_LATEST_SETTINGS_PLANS_DETAILS_SUCCESS,
    payload: { settingsPlansDetails },
  };
}

function fetchLatestSettingsPlansDetailsFailureAction() {
  return {
    type: FETCH_LATEST_SETTINGS_PLANS_DETAILS_FAILURE,
  };
}

function clearLatestSettingsPlansDetailsAction() {
  return {
    type: CLEAR_LATEST_SETTINGS_PLANS_DETAILS,
  };
}

export const SettingsPlansDetailsActionCreators = {
  fetchSettingsPlansDetailsAction,
  fetchSettingsPlansDetailsRequestAction,
  fetchSettingsPlansDetailsSuccessAction,
  renewSettingsPlansDetailsACtion,
  patchSettingsPlansPredictRequestAction,
  patchSettingsPlansPredictSuccessAction,
  patchSettingsPlansPredictFailureAction,
  fetchSettingsPlansDetailsOnYearMonthChangeAction,
  fetchLatestSettingsPlansDetailsAction,
  fetchLatestSettingsPlansDetailsRequestAction,
  fetchLatestSettingsPlansDetailsSuccessAction,
  fetchLatestSettingsPlansDetailsFailureAction,
  clearLatestSettingsPlansDetailsAction,
};

// Actions

export type FetchSettingsPlansDetailsAction = ReturnType<
  typeof fetchSettingsPlansDetailsAction
>;
export type PatchSettingsPlansPredictRequestAction = ReturnType<
  typeof patchSettingsPlansPredictRequestAction
>;
export type FetchSettingsPlansDetailsOnYearMonthChangeAction = ReturnType<
  typeof fetchSettingsPlansDetailsOnYearMonthChangeAction
>;
export type FetchLatestSettingsPlansDetailsAction = ReturnType<
  typeof fetchLatestSettingsPlansDetailsAction
>;

type SettingsPlansDetailsAction =
  | FetchSettingsPlansDetailsAction
  | ReturnType<typeof fetchSettingsPlansDetailsRequestAction>
  | ReturnType<typeof fetchSettingsPlansDetailsSuccessAction>
  | ReturnType<typeof renewSettingsPlansDetailsACtion>
  | PatchSettingsPlansPredictRequestAction
  | ReturnType<typeof patchSettingsPlansPredictSuccessAction>
  | ReturnType<typeof patchSettingsPlansPredictFailureAction>
  | FetchSettingsPlansDetailsOnYearMonthChangeAction
  | FetchLatestSettingsPlansDetailsAction
  | ReturnType<typeof fetchLatestSettingsPlansDetailsRequestAction>
  | ReturnType<typeof fetchLatestSettingsPlansDetailsSuccessAction>
  | ReturnType<typeof fetchLatestSettingsPlansDetailsFailureAction>
  | ReturnType<typeof clearLatestSettingsPlansDetailsAction>;

// State

type SettingsPlansDetailsState = {
  settingsPlansDetails?: SettingsPlansDetails;
  latestSettingsPlansDetails?: SettingsPlansDetails;
  loadingState: LoadingState;
  latestLoadingState: LoadingState;
};

const initialState: SettingsPlansDetailsState = {
  settingsPlansDetails: undefined,
  latestSettingsPlansDetails: undefined,
  loadingState: 'prepare',
  latestLoadingState: 'prepare',
};

// Selector

export function settingsPlansDetailsSelector(rootState: {
  settingsPlansDetails: SettingsPlansDetailsState;
}) {
  return rootState.settingsPlansDetails.settingsPlansDetails;
}

export function latestSettingsPlansDetailsSelector(rootState: {
  settingsPlansDetails: SettingsPlansDetailsState;
}) {
  return rootState.settingsPlansDetails.latestSettingsPlansDetails;
}

export function latestSettingsPlansDetailsLoadingStateSelector(rootState: {
  settingsPlansDetails: SettingsPlansDetailsState;
}) {
  return rootState.settingsPlansDetails.latestLoadingState;
}

// Reducer

export function settingsPlansDetailsReducer(
  state = initialState,
  action: SettingsPlansDetailsAction
): SettingsPlansDetailsState {
  switch (action.type) {
    case FETCH_SETTINGS_PLANS_DETAILS_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_SETTINGS_PLANS_DETAILS_SUCCESS:
      return {
        ...state,
        settingsPlansDetails: action.payload.settingsPlansDetails,
        loadingState: 'loaded',
      };
    case RENEW_SETTINGS_PLANS_DETAILS:
      return initialState;
    case PATCH_SETTINGS_PLANS_PREDICT_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case PATCH_SETTINGS_PLANS_PREDICT_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
      };
    case PATCH_SETTINGS_PLANS_PREDICT_FAILURE:
      return {
        ...state,
        loadingState: 'failed',
      };
    case FETCH_LATEST_SETTINGS_PLANS_DETAILS_REQUEST:
      return {
        ...state,
        latestLoadingState: 'loading',
      };
    case FETCH_LATEST_SETTINGS_PLANS_DETAILS_SUCCESS:
      return {
        ...state,
        latestSettingsPlansDetails: action.payload.settingsPlansDetails,
        latestLoadingState: 'loaded',
      };
    case FETCH_LATEST_SETTINGS_PLANS_DETAILS_FAILURE:
      return {
        ...state,
        latestSettingsPlansDetails: undefined,
        latestLoadingState: 'failed',
      };
    case CLEAR_LATEST_SETTINGS_PLANS_DETAILS:
      return {
        ...state,
        latestSettingsPlansDetails: undefined,
        latestLoadingState: 'prepare',
      };
    default:
      return state;
  }
}
